import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import { ComicGridCard } from "@/components/base/ComicCard";
import { categories } from "@/mocks/categories";
import { filterByCategory, typeDescMap, typeNameMap } from "@/utils/comics";
import type { Comic } from "@/types/comic";

const statusOptions = ["全部", "连载中", "完结"];
const sortOptions = [
  { key: "updated", label: "按更新时间" },
  { key: "popular", label: "按人气" },
  { key: "rating", label: "按评分" },
];

export default function Category() {
  const { type = "romance" } = useParams();
  const [subCategory, setSubCategory] = useState("全部");
  const [status, setStatus] = useState("全部");
  const [sort, setSort] = useState("updated");
  const [visible, setVisible] = useState(8);
  const [showFilters, setShowFilters] = useState(false);

  const name = typeNameMap[type] ?? "全部韩漫";
  const desc = typeDescMap[type] ?? "浏览全部韩漫作品。";

  const list = useMemo(() => {
    let result = filterByCategory(type);
    if (subCategory !== "全部") {
      result = result.filter((c) => c.categories.includes(subCategory));
    }
    if (status !== "全部") {
      result = result.filter((c) => c.status === status);
    }
    result = [...result].sort((a, b) => {
      if (sort === "popular") return b.views - a.views;
      if (sort === "rating") return b.rating - a.rating;
      return a.updatedAt < b.updatedAt ? 1 : -1;
    });
    return result;
  }, [type, subCategory, status, sort]);

  const shown: Comic[] = list.slice(0, visible);

  const resetFilters = () => {
    setSubCategory("全部");
    setStatus("全部");
    setSort("updated");
    setVisible(8);
  };

  const changeSub = (v: string) => {
    setSubCategory(v);
    setVisible(8);
  };
  const changeStatus = (v: string) => {
    setStatus(v);
    setVisible(8);
  };

  const filterPanel = (
    <div className="space-y-5">
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-foreground-500">
          题材细分
        </p>
        <div className="flex flex-wrap gap-2">
          {["全部", ...categories.map((c) => c.name)].map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => changeSub(c)}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                subCategory === c
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-700"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-foreground-500">
          更新状态
        </p>
        <div className="flex flex-wrap gap-2">
          {statusOptions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => changeStatus(s)}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                status === s
                  ? "bg-accent-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-accent-50 hover:text-accent-800"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-foreground-500">
          排序方式
        </p>
        <div className="flex flex-wrap gap-2">
          {sortOptions.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setSort(s.key)}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                sort === s.key
                  ? "bg-secondary-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-secondary-50 hover:text-secondary-900"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Seo
        title={`${name}韩漫漫画在线阅读 - 韩漫免费观看`}
        description={`${desc}${name}分类下汇集精彩韩漫作品，支持免费在线阅读最新连载章节。`}
        keywords={`${name}韩漫,${name}漫画,韩漫免费观看,在线阅读`}
      />

      <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb items={[{ label: `${name}分类` }]} />

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-extrabold text-foreground-950 md:text-3xl">
              {name}韩漫
            </h1>
            <p className="mt-1.5 max-w-2xl text-sm text-foreground-600">{desc}</p>
          </div>
          <div className="rounded-lg bg-primary-50 px-4 py-2 text-sm text-primary-800">
            共 <strong className="font-bold">{list.length}</strong> 部作品
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-4">
          <aside className="lg:col-span-1">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 lg:sticky lg:top-20">
              <button
                type="button"
                onClick={() => setShowFilters((v) => !v)}
                className="flex w-full items-center justify-between text-sm font-semibold text-foreground-900 lg:hidden"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-filter-3-line text-primary-600" />
                  筛选条件
                </span>
                <i className={showFilters ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
              </button>
              <div className={`${showFilters ? "mt-4 block" : "hidden"} lg:block lg:mt-0`}>
                <div className="mb-4 hidden items-center gap-2 text-sm font-semibold text-foreground-900 lg:flex">
                  <i className="ri-filter-3-line text-primary-600" />
                  筛选条件
                </div>
                {filterPanel}
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-5 w-full whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  重置筛选
                </button>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-3">
            {shown.length > 0 ? (
              <>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                  {shown.map((comic) => (
                    <ComicGridCard key={comic.id} comic={comic} />
                  ))}
                </div>
                {visible < list.length ? (
                  <div className="mt-8 flex justify-center">
                    <button
                      type="button"
                      onClick={() => setVisible((v) => v + 8)}
                      className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                    >
                      加载更多（剩余 {list.length - visible} 部）
                    </button>
                  </div>
                ) : (
                  <p className="mt-8 text-center text-xs text-foreground-400">
                    — 已经到底啦，共 {list.length} 部作品 —
                  </p>
                )}
              </>
            ) : (
              <EmptyState
                icon="ri-emotion-sad-line"
                title="没有找到符合条件的韩漫"
                desc="当前筛选条件下暂无匹配作品，试试更换题材或重置筛选条件吧。"
                actionLabel="重置筛选"
                onAction={resetFilters}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}